/**
 * A sensor's readings over a day, a week, a month or dates chosen, from
 * `GET /sensor-devices/:id/series` (averaged into buckets, so a month draws
 * whole). Every channel sharing the chosen channel's unit is drawn, so a probe's
 * depths sit on one chart; the chosen one is drawn heavier. A soil channel with
 * levels has its dry, needs-water and wet bands shaded behind the lines.
 *
 * When the channel is linked to the valve that waters it, the valve's runs are
 * shaded too, so a run the probe never answered stands out.
 *
 * The range is in the URL (?range=, ?from=&to=) so a link reopens the same view.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chart, registerables, type Plugin } from 'chart.js';
import { RefreshCw } from 'lucide-react';
import sensorDevicesService, {
  type MoistureLevels,
  type SensorSeries,
  type SeriesRange,
  type ValveRun,
} from '../../services/sensorDevices';
import {
  dateTimeLocalToISO,
  formatDateInTz,
  formatDateTimeInTz,
  formatTimeInTz,
  getTodayInTz,
} from '../../utils/timezone';
import { BAND_FILL, type MoistureDirection } from '../../utils/soilMoisture';

Chart.register(...registerables);

export interface ChartChannel {
  kind: string | null;
  index: number | null;
  label: string;
  unit: string | null;
  color: string;
  levels?: MoistureLevels | null;
  direction?: MoistureDirection;
}

type RangeChoice = SeriesRange | 'custom';

const RANGES: Array<{ key: RangeChoice; label: string }> = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'custom', label: 'Dates' },
];

function sameChannel(a: { kind: string | null; index: number | null }, b: { channel_kind: string | null; channel_index: number | null }) {
  // A device with no channels has one line, whatever its readings are filed under.
  if (a.kind === null && a.index === null) return true;
  return (a.kind ?? null) === (b.channel_kind ?? null) && (a.index ?? null) === (b.channel_index ?? null);
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const RUN_FILL = 'rgba(37, 99, 235, 0.14)';

/** Shades the level bands across the plot and the valve's runs down it, behind the lines. */
function bandsPlugin(
  levels: MoistureLevels | null | undefined,
  direction: MoistureDirection | undefined,
  runs: ValveRun[],
  nowMs: number
): Plugin<'line'> {
  return {
    id: 'moistureBands',
    beforeDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      const y = scales.y;
      const x = scales.x;
      if (!y || !x || !chartArea) return;
      if (runs.length > 0) {
        ctx.save();
        ctx.fillStyle = RUN_FILL;
        for (const run of runs) {
          const a = Math.max(x.getPixelForValue(new Date(run.started_at).getTime()), chartArea.left);
          const endMs = run.ended_at ? new Date(run.ended_at).getTime() : nowMs;
          // At least 2px, so a ten-minute run on a month's chart still shows.
          const b = Math.min(Math.max(x.getPixelForValue(endMs), a + 2), chartArea.right);
          if (b > a) ctx.fillRect(a, chartArea.top, b - a, chartArea.bottom - chartArea.top);
        }
        ctx.restore();
      }
      if (!levels || !direction) return;
      const top = y.max;
      const bottom = y.min;
      const higher = direction === 'higher_is_drier';
      const bands: Array<{ from: number; to: number; fill: string }> = higher
        ? [
            { from: levels.dry, to: top, fill: BAND_FILL.dry },
            { from: levels.refill, to: levels.dry, fill: BAND_FILL.drying },
            ...(levels.wet != null ? [{ from: bottom, to: levels.wet, fill: BAND_FILL.wet }] : []),
          ]
        : [
            { from: bottom, to: levels.dry, fill: BAND_FILL.dry },
            { from: levels.dry, to: levels.refill, fill: BAND_FILL.drying },
            ...(levels.wet != null ? [{ from: levels.wet, to: top, fill: BAND_FILL.wet }] : []),
          ];
      ctx.save();
      for (const b of bands) {
        const lo = Math.max(Math.min(b.from, b.to), bottom);
        const hi = Math.min(Math.max(b.from, b.to), top);
        if (hi <= lo) continue;
        const yTop = y.getPixelForValue(hi);
        const yBottom = y.getPixelForValue(lo);
        ctx.fillStyle = b.fill;
        ctx.fillRect(chartArea.left, yTop, chartArea.right - chartArea.left, yBottom - yTop);
      }
      ctx.restore();
    },
  };
}

export default function SensorRangeChart({
  deviceId,
  channels,
  selected,
  timezone,
  height = 260,
  reloadKey = 0,
}: {
  deviceId: string;
  /** Every channel the device has (or one entry with null kind/index for a single-value device). */
  channels: ChartChannel[];
  /** The channel the page is showing. */
  selected: ChartChannel;
  timezone: string;
  height?: number;
  /** Change it to read the series again (a valve was just linked). */
  reloadKey?: number;
}) {
  const [params, setParams] = useSearchParams();
  const range = (RANGES.some((r) => r.key === params.get('range')) ? params.get('range') : 'day') as RangeChoice;
  const today = getTodayInTz(timezone);
  const [fromDraft, setFromDraft] = useState(params.get('from') ?? addDays(today, -7));
  const [toDraft, setToDraft] = useState(params.get('to') ?? today);
  const customFrom = params.get('from');
  const customTo = params.get('to');

  const [series, setSeries] = useState<SensorSeries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart<'line'> | null>(null);

  const setRange = (next: RangeChoice) => {
    const p = new URLSearchParams(params);
    p.set('range', next);
    if (next !== 'custom') {
      p.delete('from');
      p.delete('to');
    } else {
      p.set('from', fromDraft);
      p.set('to', toDraft);
    }
    setParams(p, { replace: true });
  };

  const applyDates = () => {
    const p = new URLSearchParams(params);
    p.set('range', 'custom');
    p.set('from', fromDraft);
    p.set('to', toDraft);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const opts =
      range === 'custom' && customFrom && customTo
        ? {
            from: dateTimeLocalToISO(`${customFrom}T00:00`, timezone),
            to: dateTimeLocalToISO(`${addDays(customTo, 1)}T00:00`, timezone),
          }
        : { range: (range === 'custom' ? 'week' : range) as SeriesRange };
    sensorDevicesService
      .getSeries(deviceId, opts)
      .then((s) => { if (active) setSeries(s); })
      .catch((e: unknown) => {
        if (!active) return;
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        setError(msg ?? 'Could not load readings.');
        setSeries(null);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [deviceId, range, customFrom, customTo, timezone, reloadKey]);

  // Lines that share the chosen channel's unit, chosen one last so it draws on top.
  const drawn = useMemo(() => {
    const peers = channels.filter((c) => (c.unit ?? '') === (selected.unit ?? ''));
    const others = peers.filter((c) => !(c.kind === selected.kind && c.index === selected.index));
    return [...others, selected];
  }, [channels, selected]);

  const runs = useMemo<ValveRun[]>(
    () =>
      series?.valve_runs?.find(
        (v) => v.channel_kind === selected.kind && v.channel_index === selected.index
      )?.runs ?? [],
    [series, selected]
  );

  const pointCount = useMemo(
    () => (series?.series ?? []).reduce((n, s) => n + s.points.length, 0),
    [series]
  );

  useEffect(() => {
    if (!canvasRef.current || !series) return;
    const spanMs = new Date(series.to).getTime() - new Date(series.from).getTime();
    const short = spanMs <= 2 * 86400 * 1000;

    const datasets = drawn.map((ch) => {
      const s = series.series.find((x) => sameChannel(ch, x));
      const isSelected = ch === selected;
      return {
        label: ch.label,
        data: (s?.points ?? []).map((p) => ({ x: new Date(p.t).getTime(), y: p.avg })),
        borderColor: ch.color,
        backgroundColor: ch.color,
        borderWidth: isSelected ? 2.5 : 1.25,
        pointRadius: 0,
        tension: 0.2,
        spanGaps: (series.bucket_seconds * 3) * 1000,
      };
    });

    const lv = selected.levels;
    const ys = lv ? [lv.dry, lv.refill, ...(lv.wet != null ? [lv.wet] : [])] : [];

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        parsing: false,
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
        scales: {
          x: {
            type: 'linear',
            min: new Date(series.from).getTime(),
            max: new Date(series.to).getTime(),
            ticks: {
              maxTicksLimit: 6,
              callback: (v) => {
                const iso = new Date(Number(v)).toISOString();
                return short
                  ? formatTimeInTz(iso, timezone)
                  : formatDateInTz(iso, timezone, { day: 'numeric', month: 'short' });
              },
            },
          },
          y: {
            suggestedMin: ys.length ? Math.min(...ys) * 0.8 : undefined,
            suggestedMax: ys.length ? Math.max(...ys) * 1.15 : undefined,
            title: { display: Boolean(selected.unit), text: selected.unit ?? '' },
          },
        },
        plugins: {
          legend: { display: datasets.length > 1, position: 'bottom', labels: { boxWidth: 12 } },
          tooltip: {
            callbacks: {
              title: (items) =>
                items[0] ? formatDateTimeInTz(new Date(items[0].parsed.x ?? 0).toISOString(), timezone) : '',
              label: (item) =>
                `${item.dataset.label}: ${Number(item.parsed.y).toFixed(1)}${selected.unit ? ` ${selected.unit}` : ''}`,
            },
          },
        },
      },
      plugins: [bandsPlugin(selected.levels, selected.direction, runs, new Date(series.to).getTime())],
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [series, drawn, selected, timezone, runs]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" role="group" aria-label="Chart range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              aria-pressed={range === r.key}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                range === r.key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {loading && <RefreshCw className="h-4 w-4 animate-spin text-gray-400" aria-label="Loading" />}
      </div>

      {range === 'custom' && (
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <label className="min-w-0 text-xs text-gray-600">
            From
            <input
              type="date"
              value={fromDraft}
              max={toDraft}
              onChange={(e) => setFromDraft(e.target.value)}
              className="mt-1 block w-40 max-w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="min-w-0 text-xs text-gray-600">
            To
            <input
              type="date"
              value={toDraft}
              min={fromDraft}
              max={today}
              onChange={(e) => setToDraft(e.target.value)}
              className="mt-1 block w-40 max-w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={applyDates}
            disabled={!fromDraft || !toDraft || fromDraft > toDraft}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Show
          </button>
        </div>
      )}

      <div style={{ height }} className="relative">
        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : !loading && pointCount === 0 ? (
          <p className="text-sm italic text-gray-500">No readings in this period.</p>
        ) : (
          <canvas ref={canvasRef} aria-label={`${selected.label} readings`} role="img" />
        )}
      </div>

      {((selected.levels && selected.direction) || runs.length > 0) && (
        <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
          {runs.length > 0 && (
            <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: 'rgba(37, 99, 235, 0.45)' }} />Valve open ({runs.length} run{runs.length === 1 ? '' : 's'})</span>
          )}
          {selected.levels && (
            <>
          <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: BAND_FILL.dry.replace('0.10', '0.45') }} />Dry</span>
          <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: BAND_FILL.drying.replace('0.10', '0.45') }} />Needs water</span>
            </>
          )}
          {selected.levels?.wet != null && (
            <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: BAND_FILL.wet.replace('0.10', '0.45') }} />Wet</span>
          )}
        </p>
      )}
    </div>
  );
}
